import { DATE_FORMAT_DATE } from '_core/dates/formats';
import { TreeTable, TreeTableColumn } from '_core/react-window/treeTable';
import { TreeTableNode } from '_core/react-window/types';
import { Link } from '_core/router5/link';
import { ISorting, SortingDirection } from '_core/sorting';
import { Ellipsis } from '_core/strings/ellipsis';
import { IContractSellSerialized } from 'contractsSell/api';
import dayjs from 'dayjs';
import {
  expeditionRequestStatusLabel,
  IExpeditionRequestSerialized,
} from 'expeditionRequests/types';
import * as React from 'react';
import { useMemo } from 'react';

import { SortingInput } from '../../../_core/sortingInput';
import { ExpeditionRequestsSortingField } from '../../../expeditionRequests/api';
import { Col, Grid, Row } from '../../../layout/contentLayout';

export interface IExpeditionRequestsTreeTableItem
  extends Omit<IExpeditionRequestSerialized, 'sellContract'> {
  sellContract: IContractSellSerialized;
}

export type ExpeditionRequestsTreeTableNodeData =
  | { kind: 'partner'; id: number; partnerName: string; protocolCount: number }
  | {
      kind: 'protocol';
      id: number;
      contract: number;
      protocolName: string;
      requestCount: number;
    }
  | { kind: 'request'; request: IExpeditionRequestsTreeTableItem };

interface IProps {
  fetchChildNodes: (
    parentNodes: Array<TreeTableNode<ExpeditionRequestsTreeTableNodeData>>
  ) => Promise<Array<TreeTableNode<ExpeditionRequestsTreeTableNodeData>>>;
  isFetching: boolean;
  listParams: string;
  nodes: Array<TreeTableNode<ExpeditionRequestsTreeTableNodeData>>;
  sorting: ISorting;
  onNodesChange: (
    newNodesOrUpdater:
      | Array<TreeTableNode<ExpeditionRequestsTreeTableNodeData>>
      | ((
          prevNodes: Array<TreeTableNode<ExpeditionRequestsTreeTableNodeData>>
        ) => Array<TreeTableNode<ExpeditionRequestsTreeTableNodeData>>)
  ) => void;
  onSortingChange: (newSorting: ISorting) => void;
}

export function ExpeditionRequestsTreeTable({
  fetchChildNodes,
  isFetching,
  listParams,
  nodes,
  sorting,
  onNodesChange,
  onSortingChange,
}: IProps) {
  const columns = useMemo(
    (): Array<TreeTableColumn<ExpeditionRequestsTreeTableNodeData>> => [
      {
        id: 'name',
        label: 'Наименование',
        defaultWidth: 650,
        sortable: true,
        expandable: true,
        copyCellContent: node => {
          switch (node.data.kind) {
            case 'partner': {
              const { partnerName, protocolCount } = node.data;

              return `${partnerName} (${protocolCount})`;
            }
            case 'protocol': {
              const { protocolName, requestCount } = node.data;

              return `${protocolName} (${requestCount})`;
            }
            case 'request': {
              return node.data.request.requestName;
            }
          }
        },
        renderCellContent: node => {
          switch (node.data.kind) {
            case 'partner': {
              const { id, partnerName, protocolCount } = node.data;

              return (
                <Ellipsis
                  component={Link}
                  params={{ id }}
                  rel="noopener"
                  target="_blank"
                  to="partners.edit"
                >
                  {`${partnerName} (${protocolCount})`}
                </Ellipsis>
              );
            }
            case 'protocol': {
              const { contract, id, protocolName, requestCount } = node.data;

              return (
                <Ellipsis
                  component={Link}
                  params={{
                    id: contract,
                    protocolId: id,
                    showArchived: true,
                  }}
                  rel="noopener"
                  target="_blank"
                  to="contracts.sell.view.expeditionProtocols.view"
                >
                  {`${protocolName} (${requestCount})`}
                </Ellipsis>
              );
            }
            case 'request': {
              const request = node.data.request;

              return (
                <Ellipsis
                  component={Link}
                  params={{ id: request.id, listParams }}
                  rel="noopener"
                  target="_blank"
                  to="expeditions.requests.view"
                >
                  {request.requestName}
                </Ellipsis>
              );
            }
          }
        },
      },
      {
        id: 'startDate',
        label: 'Дата начала',
        defaultWidth: 140,
        sortable: true,
        copyCellContent: node => {
          if (node.data.kind !== 'request') {
            return '';
          }

          const request = node.data.request;

          return dayjs(request.startDate).format(DATE_FORMAT_DATE);
        },
        renderCellContent: node => {
          if (node.data.kind !== 'request') {
            return null;
          }

          const request = node.data.request;

          return dayjs(request.startDate).format(DATE_FORMAT_DATE);
        },
      },
      {
        id: 'endDate',
        label: 'Дата окончания',
        defaultWidth: 140,
        sortable: true,
        copyCellContent: node => {
          if (node.data.kind !== 'request') {
            return '';
          }

          const request = node.data.request;

          return dayjs(request.endDate).format(DATE_FORMAT_DATE);
        },
        renderCellContent: node => {
          if (node.data.kind !== 'request') {
            return null;
          }

          const request = node.data.request;

          return dayjs(request.endDate).format(DATE_FORMAT_DATE);
        },
      },
      {
        id: 'status',
        label: 'Статус заявки',
        defaultWidth: 150,
        copyCellContent: node => {
          if (node.data.kind !== 'request') {
            return '';
          }

          const request = node.data.request;

          return expeditionRequestStatusLabel(request.status);
        },
        renderCellContent: node => {
          if (node.data.kind !== 'request') {
            return null;
          }

          const request = node.data.request;

          return expeditionRequestStatusLabel(request.status);
        },
      },
      {
        id: 'contract',
        label: 'Договор',
        defaultWidth: 150,
        copyCellContent: node => {
          if (node.data.kind !== 'request') {
            return '';
          }

          return node.data.request.sellContract.number;
        },
        renderCellContent: node => {
          if (node.data.kind !== 'request') {
            return null;
          }

          const request = node.data.request;

          return (
            <Ellipsis
              component={Link}
              params={{ id: request.sellContract.id }}
              rel="noopener"
              target="_blank"
              to="contracts.sell.view"
            >
              {request.sellContract.number}
            </Ellipsis>
          );
        },
      },
    ],

    [listParams]
  );

  // ExpeditionRequestsSortingField

  const defaultSorting: ISorting<ExpeditionRequestsSortingField> = {
    field: ExpeditionRequestsSortingField.StartDate,
    direction: SortingDirection.Descending,
  };

  return (
    <>
      <Row>
        <Grid>
          <Col align="end">
            <SortingInput
              defaultValue={defaultSorting}
              options={columns
                .filter(column => column.sortable)
                .map(column => ({
                  label: column.label,
                  value: column.id as ExpeditionRequestsSortingField,
                }))}
              value={sorting}
              onChange={onSortingChange}
            />
          </Col>
        </Grid>
      </Row>

      <Row stretch>
        <TreeTable
          columns={columns}
          fetchChildNodes={fetchChildNodes}
          isFetching={isFetching}
          nodes={nodes}
          sorting={sorting}
          onNodesChange={onNodesChange}
          onSortingChange={onSortingChange}
        />
      </Row>
    </>
  );
}
